/**
 * CustomerSelector
 *
 * Shows available customers as cards with their VPN/DB status.
 * Allows connecting, disconnecting, and selecting which customer
 * the rest of the ETL UI is scoped to.
 */

function StatusDot({ ok, className = '' }) {
  return (
    <span
      className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${ok ? 'bg-emerald-400' : 'bg-gray-300'} ${className}`}
    />
  );
}

function StatusBadge({ status }) {
  const map = {
    active:      { label: 'Active',      cls: 'bg-emerald-100 text-emerald-700' },
    connecting:  { label: 'Connecting…', cls: 'bg-blue-100 text-blue-700' },
    error:       { label: 'Error',       cls: 'bg-red-100 text-red-600' },
    inactive:    { label: 'Inactive',    cls: 'bg-gray-100 text-gray-500' },
  };
  const { label, cls } = map[status] ?? map.inactive;
  return (
    <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${cls}`}>
      {label}
    </span>
  );
}

export default function CustomerSelector({
  customers,
  selectedCustomerId,
  connectingId,
  disconnectingId,
  loading,
  onConnect,
  onDisconnect,
  onSelect,
}) {
  if (loading && customers.length === 0) {
    return (
      <div className="mb-5 bg-white/10 rounded-xl p-4">
        <p className="text-white/50 text-sm">Loading customers…</p>
      </div>
    );
  }

  if (customers.length === 0) {
    return (
      <div className="mb-5 bg-white/10 rounded-xl p-4">
        <p className="text-white/70 text-sm">
          No customers configured. Add entries to{' '}
          <code className="bg-white/10 px-1 rounded text-xs">etl-service/customers/customers.json</code>.
        </p>
      </div>
    );
  }

  return (
    <div className="mb-5">
      <p className="text-white/40 text-[10px] font-semibold uppercase tracking-widest mb-3">
        Customers
      </p>
      <div className="flex flex-wrap gap-3">
        {customers.map((customer) => {
          const { customer_id, display_name, status, vpn_status, db_status, error } = customer;
          const isSelected = customer_id === selectedCustomerId;
          const isActive = status === 'active';
          const isConnecting = connectingId === customer_id;
          const isDisconnecting = disconnectingId === customer_id;
          const isBusy = isConnecting || isDisconnecting;

          return (
            <div
              key={customer_id}
              className={`relative min-w-[200px] rounded-xl p-4 transition-all ${
                isSelected
                  ? 'bg-white ring-2 ring-white shadow-lg'
                  : isActive
                  ? 'bg-white/90 hover:bg-white cursor-pointer'
                  : 'bg-white/15 hover:bg-white/20'
              }`}
              onClick={() => {
                if (isActive && !isSelected) onSelect(customer_id);
              }}
            >
              {/* Header */}
              <div className="flex items-start justify-between gap-2 mb-2">
                <span className={`text-sm font-semibold leading-tight ${isSelected ? 'text-gray-900' : isActive ? 'text-gray-800' : 'text-white'}`}>
                  {display_name}
                </span>
                <StatusBadge status={status} />
              </div>

              {/* Health indicators (only when active or connecting) */}
              {(isActive || isConnecting) && (
                <div className="space-y-1 mb-3">
                  <div className="flex items-center gap-1.5">
                    {isConnecting ? (
                      <span className="w-2 h-2 rounded-full border border-blue-400 border-t-transparent animate-spin flex-shrink-0" />
                    ) : (
                      <StatusDot ok={vpn_status === 'connected'} />
                    )}
                    <span className={`text-[11px] ${isSelected ? 'text-gray-500' : 'text-gray-600'}`}>
                      VPN: {isConnecting ? 'connecting…' : (vpn_status ?? '—')}
                    </span>
                  </div>
                  {!isConnecting && (
                    <div className="flex items-center gap-1.5">
                      <StatusDot ok={db_status === 'connected'} />
                      <span className={`text-[11px] ${isSelected ? 'text-gray-500' : 'text-gray-600'}`}>
                        DB: {db_status ?? '—'}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Error */}
              {status === 'error' && error && (
                <p className="text-[10px] text-red-500 mb-2 leading-snug">{error}</p>
              )}

              {/* Actions */}
              <div className="flex items-center gap-2 mt-1">
                {isActive ? (
                  <>
                    {isSelected ? (
                      <span className="text-[10px] font-semibold text-primary-600 bg-primary-50 px-2 py-0.5 rounded-full">
                        Selected
                      </span>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); onSelect(customer_id); }}
                        className="text-[10px] font-semibold text-primary-600 hover:text-primary-700 bg-primary-50 hover:bg-primary-100 px-2 py-0.5 rounded-full transition-colors"
                      >
                        Select
                      </button>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); onDisconnect(customer_id); }}
                      disabled={isBusy}
                      className="text-[10px] font-medium text-gray-400 hover:text-red-500 disabled:opacity-40 transition-colors"
                    >
                      {isDisconnecting ? 'Stopping…' : 'Disconnect'}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={(e) => { e.stopPropagation(); onConnect(customer_id); }}
                    disabled={isBusy}
                    className="flex items-center gap-1.5 text-[11px] font-medium px-3 py-1 rounded-lg bg-white/20 hover:bg-white/30 disabled:opacity-50 text-white transition-colors"
                  >
                    {isConnecting ? (
                      <>
                        <span className="w-3 h-3 rounded-full border border-white/40 border-t-white animate-spin" />
                        Connecting…
                      </>
                    ) : (
                      <>
                        {status === 'error' ? 'Retry' : 'Connect'}
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
